import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Typography,
    InputBase,
    MenuItem,
    Select,
    CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { MyAddRoundedIcon } from "../../Assets/svg/MyIcons";

import { useNavigate } from "react-router-dom";
import BreadcrumbsComponent from "../../components/Common/Breadcrumbs";
import CustomSnackbar from "../Common/snackBar";
import { getPermissionData, getUserData } from "../../Actions/common";
import {
    getModulesAction,
    listTutorialsAction,
    getPopularArticlesAction,
} from "../../Actions/KnowledgeBaseAction";
import KnowledgeBaseFormDialog from "./KnowledgeBaseFormDialog";
import KBListItem from "../../components/KnowledgeBase/KBListItem";
import ModuleCard from "../../components/KnowledgeBase/ModuleCard";

const countAttachmentsByType = (attachments) => {
    const list = Array.isArray(attachments) ? attachments : [];
    const videos = list.filter((a) => a.mediaType === "video").length;
    const images = list.filter((a) => a.mediaType === "image").length;
    const docs = list.filter(
        (a) => a.mediaType === "document" || a.mediaType === "raw",
    ).length;
    return { videos, images, docs };
};

const DEFAULT_MODULE_VISIBLE = 5;

const KnowledgeBaseSection = () => {
    const navigate = useNavigate();

    // Permissions
    const userData = localStorage.getItem("userData") ? getUserData() : null;
    const permissionData = localStorage.getItem(
        `${userData?.id}_permissionData`,
    )
        ? getPermissionData(`${userData?.id}_permissionData`)
        : [];
    const hasPermission = permissionData[0]?.role_permissions?.find(
        (m) => m?.module && m?.module?.slug === "knowledge-base",
    );

    const [modules, setModules] = useState([]);
    const [popularArticles, setPopularArticles] = useState([]);
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [loading, setLoading] = useState(true);

    const [search, setSearch] = useState("");
    const [moduleFilter, setModuleFilter] = useState("all");
    const [browseExpanded, setBrowseExpanded] = useState(false);

    // Dialogs
    const [formDialog, setFormDialog] = useState({
        open: false,
        tutorial: null,
    });
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const showSnack = (message, severity = "success") =>
        setSnackbar({ open: true, message, severity });

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [mods, pops] = await Promise.all([
                getModulesAction(),
                getPopularArticlesAction(),
            ]);
            setModules(Array.isArray(mods) ? mods : []);
            setPopularArticles(Array.isArray(pops) ? pops : []);
        } catch (error) {
            showSnack("Failed to load knowledge base data.", "error");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Search effect
    useEffect(() => {
        if (!search.trim()) {
            setIsSearching(false);
            setSearchResults([]);
            return;
        }
        const timer = setTimeout(async () => {
            setIsSearching(true);
            try {
                const res = await listTutorialsAction({
                    search: search.trim(),
                    moduleSlug: moduleFilter === "all" ? "" : moduleFilter,
                });
                setSearchResults(Array.isArray(res) ? res : res.rows || []);
            } catch (err) {
                console.error(err);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [search, moduleFilter]);
    const browseModules = modules.filter((m) => m.articleCount > 0);

    return (
        <Box id="kt_content_container" sx={{ pb: 10 }}>
            <BreadcrumbsComponent />

            {/* HERO SECTION */}
            <Box
                sx={{
                    background:
                        "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)", // Matches tailwind from-blue-900 to-blue-500
                    borderRadius: 3,
                    p: { xs: 4, md: 8 },
                    mb: 6,
                    mt: 2,
                    textAlign: "left",
                    color: "white",
                    position: "relative",
                }}
            >
                <Typography
                    variant="h3"
                    fontWeight="bold"
                    mb={2}
                    sx={{ color: "white" }}
                >
                    How can we help you?
                </Typography>
                <Typography
                    variant="subtitle1"
                    sx={{ opacity: 0.9, mb: 4, color: "white" }}
                >
                    Search our knowledge base or browse categories below to find
                    what you need.
                </Typography>
                {/* Search Bar */}
                <Box
                    sx={{
                        display: "flex",
                        maxWidth: { xs: "100%", md: 820 },
                        width: "100%",
                        bgcolor: "background.paper",
                        borderRadius: 2,
                        p: 0.5,
                        boxShadow: 3,
                        gap: 1,
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            px: { xs: 1, md: 1.5 },
                            gap: 1,
                            minHeight: 48,
                        }}
                    >
                            <Box sx={{ bgcolor: '#eef2ff', borderRadius: '50%', p: 0.9, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 0.5 }}>
                                <SearchIcon sx={{ color: '#0b63d6', fontSize: 20 }} />
                            </Box>
                            <InputBase
                                placeholder="Search for knowledge base..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                sx={{ width: "100%", color: "text.primary", fontSize: 15, py: 0.5 }}
                                inputProps={{ 'aria-label': 'search-knowledgebase' }}
                            />
                        {/* no submit button: search runs on input change */}
                    </Box>
                    <Box sx={{ borderLeft: "1px solid #e0e0e0", px: 1.25, display: 'flex', alignItems: 'center' }}>
                        <Select
                            value={moduleFilter}
                            onChange={(e) => setModuleFilter(e.target.value)}
                            variant="standard"
                            disableUnderline
                            sx={{ minWidth: 140, px: 1, color: "text.primary" }}
                            MenuProps={{ PaperProps: { sx: { maxHeight: 250 } } }}
                        >
                            <MenuItem value="all">All Modules</MenuItem>
                            {modules.map((m) => (
                                <MenuItem key={m.slug} value={m.slug}>
                                    {m.moduleName}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                </Box>

                {/* Decorative large icon on right */}
                <Box
                    sx={{
                        position: 'absolute',
                        right: { xs: 12, md: 24 },
                        top: '50%',
                        transform: 'translateY(-50%)',
                        opacity: 0.32,
                        display: { xs: 'none', sm: 'block' },
                        pointerEvents: 'none',
                        width: 240,
                        height: 160,
                        zIndex: 0,
                    }}
                >
                    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                        <MenuBookIcon sx={{ fontSize: 160, color: 'white', opacity: 0.22 }} />
                        <Box sx={{ position: 'absolute', right: 14, bottom: 14, bgcolor: '#0b63d6', width: 64, height: 64, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 4, border: '4px solid rgba(255,255,255,0.12)' }}>
                            <SearchIcon sx={{ color: 'white', fontSize: 30 }} />
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* SEARCH RESULTS */}
            {isSearching && (
                <Box mb={6}>
                    <Typography variant="h5" fontWeight={600} mb={2}>
                        Search Results
                    </Typography>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                md: "repeat(2, 1fr)",
                            },
                            columnGap: 1,
                            rowGap: 0.5,
                        }}
                    >
                        {searchResults.length > 0 ? (
                            searchResults.map((article) => (
                                <KBListItem
                                    key={article.id}
                                    article={article}
                                    onClick={() => navigate(`/knowledge-base/${article.moduleSlug}/${article.id}`)}
                                />
                            ))
                        ) : (
                            <Box sx={{ gridColumn: "1 / -1" }}>
                                <Typography color="text.secondary">
                                    No knowledge base found matching "{search}"
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            )}

            {/* BROWSE BY MODULE */}
            {!isSearching && (
                <Box mb={6}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 2,
                        }}
                    >
                        <Typography variant="h5" fontWeight={600}>
                            Browse by Module
                        </Typography>
                        {hasPermission?.canCreate && (
                            <button
                                className="btn btn-outline btn-primary btn-add"
                                onClick={() =>
                                    setFormDialog({
                                        open: true,
                                        tutorial: null,
                                    })
                                }
                            >
                                <MyAddRoundedIcon className="me-1 text-white" />{" "}
                                ADD
                            </button>
                        )}
                    </Box>

                    <Box
                        sx={{
                            px: 1,
                            py: 1,
                        }}
                    >
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    sm: "repeat(2, 1fr)",
                                    md: "repeat(3, 1fr)",
                                    lg: "repeat(5, 1fr)",
                                },
                                gap: 3,
                            }}
                        >
                            {loading ? (
                                <Box
                                    sx={{
                                        gridColumn: "1 / -1",
                                        display: "flex",
                                        justifyContent: "center",
                                        p: 4,
                                    }}
                                >
                                    <CircularProgress />
                                </Box>
                            ) : browseModules.length > 0 ? (
                                (browseExpanded ? browseModules : browseModules.slice(0, DEFAULT_MODULE_VISIBLE)).map((m) => (
                                    <Box key={m.id} sx={{ height: '100%' }}>
                                        <ModuleCard moduleItem={m} onClick={() => navigate(`/knowledge-base/${m.slug}`)} />
                                    </Box>
                                ))
                            ) : (
                                <Box sx={{ gridColumn: "1 / -1" }}>
                                    <Typography
                                        align="center"
                                        color="text.secondary"
                                    >
                                        No modules available.
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {browseModules.length > 0 &&
                        browseModules.length > DEFAULT_MODULE_VISIBLE && (
                            <Box mt={2} sx={{ textAlign: "right" }}>
                                <Typography
                                    component="span"
                                    onClick={() => setBrowseExpanded((p) => !p)}
                                    sx={{
                                        color: "#22419e",
                                        fontWeight: 700,
                                        cursor: "pointer",
                                        fontSize: "1.2rem",
                                        "&:hover": {
                                            textDecoration: "underline",
                                        },
                                    }}
                                >
                                    {browseExpanded ? "Collapse ↑" : "View All →"}
                                </Typography>
                            </Box>
                        )}
                </Box>
            )}

            {/* POPULAR ARTICLES */}
            {!isSearching && popularArticles.length > 0 && (
                <Box>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h5" fontWeight={600}>
                            Popular Knowledge Base
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                md: "repeat(2, 1fr)",
                            },
                            columnGap: 1,
                            rowGap: 0.5,
                            mb: 2,
                        }}
                    >
                        {popularArticles.map((article) => (
                            <KBListItem
                                key={article.id}
                                article={article}
                                onClick={() => navigate(`/knowledge-base/${article.moduleSlug}/${article.id}`)}
                            />
                        ))}
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                        <Typography
                            component="span"
                            onClick={() => navigate("/knowledge-base/all")}
                            sx={{
                                color: "#22419e",
                                fontWeight: 700,
                                cursor: "pointer",
                                fontSize: "1.2rem",
                                "&:hover": { textDecoration: "underline" },
                            }}
                        >
                            View All →
                        </Typography>
                    </Box>
                </Box>
            )}

            <KnowledgeBaseFormDialog
                open={formDialog.open}
                onClose={() => setFormDialog({ open: false, tutorial: null })}
                tutorial={formDialog.tutorial}
                modules={modules}
                onSaved={() => {
                    loadData();
                    setFormDialog({ open: false, tutorial: null });
                    showSnack(
                        formDialog.tutorial
                            ? "Knowledge Base updated"
                            : "Knowledge Base created",
                        "success",
                    );
                }}
            />

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
            />
        </Box>
    );
};

export default KnowledgeBaseSection;
